import React from 'react';
import { styled } from '@mui/material/styles';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import {
  ClientVertical,
  ParentOrganization,
} from '../../../data/models/client.model';
import SelectMenu from '../../forms/SelectMenu';
import TextInput from '../../forms/TextInput';
import DateInput from '../../forms/DateInput';
import Checkbox from '../../forms/Checkbox';
import { DateTime } from 'luxon';

const PREFIX = 'ProfileForm';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  paper: `${PREFIX}-paper`,
  form: `${PREFIX}-form`,
  purpose: `${PREFIX}-purpose`,
  subctrl: `${PREFIX}-subctrl`,
  addButton: `${PREFIX}-addButton`,
  mapWrapper: `${PREFIX}-mapWrapper`,
  effectiveDates: `${PREFIX}-effectiveDates`,
  internalClient: `${PREFIX}-internalClient`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
    marginBottom: theme.spacing(4),
  },

  [`& .${classes.paper}`]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
    gap: theme.spacing(2),
    width: 500,
  },

  [`& .${classes.purpose}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },

  [`& .${classes.subctrl}`]: {
    marginLeft: theme.spacing(4),
  },

  [`& .${classes.addButton}`]: {
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.effectiveDates}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },

  [`& .${classes.internalClient}`]: {
    color: theme.palette.lightActionDisabled90p.main,
  },
  '& .MuiTypography-body1': {
    fontSize: '16px !important',
  },
}));

interface ProfileFormProps {
  isNew: boolean;
  parentOrganizations: ParentOrganization[];
  clientVerticals: ClientVertical[];
  formHook: UseFormReturn<any, any>;
}

export default function ProfileForm({
  isNew,
  parentOrganizations,
  clientVerticals,
  formHook,
}: ProfileFormProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');

  const { control, formState } = formHook;

  const effectiveStart = useWatch({
    control: formHook.control,
    name: 'EffectiveStartDate',
  });

  const effectiveEnd = useWatch({
    control: formHook.control,
    name: 'EffectiveEndDate',
  });

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.form}>
        <SelectMenu
          label={t('client.parentOrganization')}
          name="ParentOrganization"
          values={parentOrganizations}
          valueAttr="parentOrganizationBC"
          labelAttr="parentOrganizationName"
          required={true}
          disabled={!isNew}
          control={control}
          errorMessage={formState.errors.ParentOrganization?.message as string}
          rules={{ required: requiredFieldMessage }}
        />
        <TextInput
          name="ClientBC"
          label={t('client.clientBC')}
          control={formHook.control}
          required={true}
          disabled={!isNew}
          rules={{ required: requiredFieldMessage }}
          tooltip="Client Business Code"
          tooltipPlacement="right"
        />
        <TextInput
          name="ClientLegalName"
          label={t('client.clientLegalName')}
          control={formHook.control}
          required={true}
          disabled={false}
          rules={{ required: requiredFieldMessage }}
        />
        <TextInput
          name="Abbreviation"
          label={t('client.abbreviation')}
          control={formHook.control}
          required={true}
          disabled={false}
          rules={{ required: requiredFieldMessage }}
          tooltip="Company Abbreviation"
          tooltipPlacement="right"
        />
        <SelectMenu
          label={t('client.vertical')}
          name="Vertical"
          values={clientVerticals}
          valueAttr="clientVerticalBC"
          labelAttr="clientVerticalName"
          required={true}
          control={control}
          rules={{ required: requiredFieldMessage }}
        />
        <Box className={classes.effectiveDates}>
          <DateInput
            label={t('client.effectiveStart')}
            name="EffectiveStartDate"
            control={formHook.control}
            errors={formState.errors}
            minDate={DateTime.fromISO('1970-01-01')}
            maxDate={
              effectiveEnd
                ? DateTime.fromISO(effectiveEnd)
                : DateTime.fromISO('2099-12-31')
            }
          />
          <DateInput
            label={t('client.effectiveEnd')}
            name="EffectiveEndDate"
            control={formHook.control}
            errors={formState.errors}
            minDate={
              effectiveStart
                ? DateTime.fromISO(effectiveStart)
                : DateTime.fromISO('1970-01-01')
            }
          />
        </Box>
        <Checkbox
          label={t('client.internalClient')}
          name="isInternal"
          className={classes.internalClient}
          control={formHook.control}
          defaultValue={false}
        />
      </Box>
    </StyledBox>
  );
}
